import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { SystemSettingDto } from '@models/system-setting-dto.interface';
import { CreateUpdateSystemSettingDto } from '@models/create-update-system-setting-dto.interface';
import { DefaultTestsSettingDto } from '@models/default-tests-setting-dto.interface';
import { FluidConstantsSettingDto } from '@models/fluid-constants-setting-dto.interface';

@Injectable({
  providedIn: 'root',
})
export class SystemSettingsService {
  private readonly apiService = inject(ApiService);

  // Generic settings endpoints
  getAllSettings(): Observable<SystemSettingDto[]> {
    return this.apiService.get('settings');
  }

  getSettingById(id: number): Observable<SystemSettingDto> {
    return this.apiService.get(`settings/${id}`);
  }

  getSettingByKey(settingKey: string): Observable<SystemSettingDto> {
    return this.apiService.get(`settings/key/${encodeURIComponent(settingKey)}`);
  }

  getSettingsByCategory(category: string): Observable<SystemSettingDto[]> {
    return this.apiService.get(`settings/category/${encodeURIComponent(category)}`);
  }

  createSetting(dto: CreateUpdateSystemSettingDto): Observable<SystemSettingDto> {
    return this.apiService.post('settings', dto);
  }

  updateSetting(id: number, dto: CreateUpdateSystemSettingDto): Observable<SystemSettingDto> {
    return this.apiService.put(`settings/${id}`, dto);
  }

  deleteSetting(id: number): Observable<void> {
    return this.apiService.delete(`settings/${id}`);
  }

  getWbmDefaultTests(): Observable<DefaultTestsSettingDto> {
    return this.apiService.get('settings/defaults/wbm-tests');
  }

  getBusinessUnitsWithDeepwaterFormat(): Observable<number[]> {
    return this.apiService.get('settings/defaults/business-units-deepwater-format');
  }

  updateBusinessUnitsWithDeepwaterFormat(buIds: number[]): Observable<number[]> {
    return this.apiService.post('settings/defaults/business-units-deepwater-format', buIds);
  }

  updateWbmDefaultTests(testIds: number[]): Observable<DefaultTestsSettingDto> {
    return this.apiService.post('settings/defaults/wbm-tests', testIds);
  }

  getNafDefaultTests(): Observable<DefaultTestsSettingDto> {
    return this.apiService.get('settings/defaults/naf-tests');
  }

  updateNafDefaultTests(testIds: number[]): Observable<DefaultTestsSettingDto> {
    return this.apiService.post('settings/defaults/naf-tests', testIds);
  }

  // Specialized endpoints for fluid constants
  getFluidConstants(fluidType: 'WBM' | 'SOBM' | 'DOBM'): Observable<FluidConstantsSettingDto> {
    return this.apiService.get(`settings/constants/${fluidType}`);
  }

  updateFluidConstants(constants: FluidConstantsSettingDto): Observable<FluidConstantsSettingDto> {
    return this.apiService.post('settings/constants', constants);
  }

  /**
   * Get all fluid constants for all fluid types
   */
  getAllFluidConstants(): Observable<FluidConstantsSettingDto[]> {
    const fluidTypes: Array<'WBM' | 'SOBM' | 'DOBM'> = ['WBM', 'SOBM', 'DOBM'];
    return new Observable(observer => {
      const constants: FluidConstantsSettingDto[] = [];
      let completed = 0;

      fluidTypes.forEach(fluidType => {
        this.getFluidConstants(fluidType).subscribe({
          next: (constant) => {
            constants.push(constant);
            completed++;
            if (completed === fluidTypes.length) {
              observer.next(constants);
              observer.complete();
            }
          },
          error: (err) => {
            completed++;
            if (completed === fluidTypes.length) {
              observer.next(constants);
              observer.complete();
            }
          },
        });
      });
    });
  }
}
